import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import posthogPiniaPlugin from '@/composables/pinia-posthog.js';
import { reportError } from '@/services/errorReporting.ts';

import logger from '@/util/logging/logger.js';
import { registerAsyncGlobalComponents } from '@/plugins/registerAsyncGlobalComponents.js';
import { registerUiComponents } from '@/plugins/registerUiComponents.js';

import '@/assets/tailwind.css';

import { registerFontAwesome } from '@/plugins/registerFontAwesome.js';
import { initStores, stores } from '@/util/stores';
import { AUTH_SESSION_CLEARED_EVENT, AUTH_SESSION_UPDATED_EVENT } from '@/util/authSession.ts';
import { syncDarkThemeClass } from '@/layout/composables/layout.js';

const app = createApp(App);
const pinia = createPinia();
pinia.use(posthogPiniaPlugin);

app.use(pinia);
initStores(pinia);

stores.authStore.initializeAuthState();

router.afterEach((to) => {
	syncDarkThemeClass(to.matched.some((record) => record.meta?.auth === true));
});

window.addEventListener(AUTH_SESSION_CLEARED_EVENT, async () => {
	if (router.currentRoute.value.name === 'login') {
		return;
	}

	try {
		await router.push({ name: 'login' });
	} catch {
		window.location.href = '/login';
	}
});

window.addEventListener(AUTH_SESSION_UPDATED_EVENT, async (event) => {
	if (!(event instanceof CustomEvent) || event.detail?.source !== 'remote') {
		return;
	}

	const currentRoute = router.currentRoute.value;
	if (!currentRoute.meta?.auth) {
		return;
	}

	try {
		await router.replace({
			path: currentRoute.path,
			query: currentRoute.query,
			hash: currentRoute.hash,
			force: true
		});
	} catch (error) {
		logger.warn('Failed to refresh route after cross-tab auth update', { error });
	}
});

app.config.errorHandler = (err, instance, info) => {
	const component = (instance && instance.$ && (instance.$.type?.name || instance.$.type?.__name)) || 'unknown';
	reportError(err, {
		source: 'vue',
		domain: 'component',
		operation: String(info || 'application_error'),
		component: String(component),
		routeName: typeof router.currentRoute.value.name === 'string' ? router.currentRoute.value.name : null,
		routePattern: router.currentRoute.value.matched.at(-1)?.path ?? router.currentRoute.value.path,
		handled: false
	});
	logger.error('Vue application error', { error: err, component, info });
};

window.addEventListener('unhandledrejection', (event) => {
	logger.error('Unhandled Promise Rejection:', event.reason);
	reportError(event.reason, {
		source: 'window',
		domain: 'promise',
		operation: 'unhandled_rejection',
		routeName: typeof router.currentRoute.value.name === 'string' ? router.currentRoute.value.name : null,
		routePattern: router.currentRoute.value.matched.at(-1)?.path ?? router.currentRoute.value.path,
		handled: false
	});
});

window.addEventListener('error', (event) => {
	reportError(event.error ?? event.message, {
		source: 'window',
		domain: 'runtime',
		operation: 'uncaught_error',
		routeName: typeof router.currentRoute.value.name === 'string' ? router.currentRoute.value.name : null,
		routePattern: router.currentRoute.value.matched.at(-1)?.path ?? router.currentRoute.value.path,
		handled: false,
		details: {
			filename: event.filename,
			line: event.lineno,
			column: event.colno
		}
	});
});

app.config.globalProperties.$logger = logger;

registerAsyncGlobalComponents(app);
registerUiComponents(app);
registerFontAwesome(app);
app.use(router);

app.mount('#app');
